import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { isPushSupported, registerPush, unregisterPush, getCurrentSubscription } from '../utils/pushManager';

export default function Settings() {
  const { user } = useAuth();
  const [showPw, setShowPw] = useState(false);
  const [pw, setPw] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [pwMsg, setPwMsg] = useState(null);
  const [pwError, setPwError] = useState(null);
  const [pushState, setPushState] = useState({ supported: false, subscribed: false, enabled: false, loading: true, permission: 'default' });
  const [testMsg, setTestMsg] = useState(null);

  useEffect(() => {
    async function checkPush() {
      if (!isPushSupported()) {
        setPushState(s => ({ ...s, supported: false, loading: false }));
        return;
      }
      try {
        const sub = await getCurrentSubscription();
        const { data } = await api.get('/api/push/settings');
        setPushState({
          supported: true,
          subscribed: !!sub,
          enabled: data.pushEnabled,
          loading: false,
          permission: Notification.permission
        });
      } catch {
        setPushState(s => ({ ...s, supported: true, loading: false, permission: Notification.permission }));
      }
    }
    checkPush();
  }, []);

  async function changePassword(e) {
    e.preventDefault();
    setPwMsg(null); setPwError(null);
    if (pw.newPassword !== pw.confirm) { setPwError('New passwords do not match'); return; }
    if (pw.newPassword.length < 6) { setPwError('New password must be at least 6 characters'); return; }
    try {
      await api.put('/api/auth/change-password', { currentPassword: pw.currentPassword, newPassword: pw.newPassword });
      setPw({ currentPassword: '', newPassword: '', confirm: '' });
      setPwMsg('Password changed successfully');
      setTimeout(() => { setPwMsg(null); setShowPw(false); }, 2000);
    } catch (err) {
      setPwError(err.response?.data?.error || 'Failed to change password');
    }
  }

  return (
    <div>
      <h2 className="page-title" style={{ marginBottom: '20px' }}>Settings</h2>

      {/* Profile */}
      <div className="card">
        <h3 className="card-title" style={{ marginBottom: '16px' }}>Profile</h3>
        <div className="auto-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div><label>Name</label><div>{user?.name}</div></div>
          <div><label>Email</label><div>{user?.email}</div></div>
          <div><label>Role</label><div><span className="badge badge-info">{user?.role}</span></div></div>
          <div>
            <button className="btn btn-secondary btn-sm"
              onClick={() => { setShowPw(!showPw); setPwError(null); setPwMsg(null); setPw({ currentPassword: '', newPassword: '', confirm: '' }); }}>
              {showPw ? 'Cancel' : 'Change Password'}
            </button>
          </div>
        </div>
        {showPw && (
          <form onSubmit={changePassword} style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border)' }}>
            <div className="form-group">
              <label>Current Password</label>
              <input type="password" value={pw.currentPassword} onChange={e => setPw({ ...pw, currentPassword: e.target.value })} required style={{ maxWidth: '300px' }} />
            </div>
            <div className="form-group">
              <label>New Password</label>
              <input type="password" value={pw.newPassword} onChange={e => setPw({ ...pw, newPassword: e.target.value })} required style={{ maxWidth: '300px' }} />
            </div>
            <div className="form-group">
              <label>Confirm New Password</label>
              <input type="password" value={pw.confirm} onChange={e => setPw({ ...pw, confirm: e.target.value })} required style={{ maxWidth: '300px' }} />
            </div>
            {pwError && <p style={{ color: 'var(--danger)', fontSize: '13px', marginBottom: '8px' }}>{pwError}</p>}
            {pwMsg && <p style={{ color: 'var(--success)', fontSize: '13px', marginBottom: '8px' }}>{pwMsg}</p>}
            <button type="submit" className="btn btn-primary">Save</button>
          </form>
        )}
      </div>

      {/* Push Notifications */}
      <div className="card">
        <h3 className="card-title" style={{ marginBottom: '16px' }}>Notifications</h3>
        {pushState.loading ? (
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Checking push notification status...</p>
        ) : !pushState.supported ? (
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
            Push notifications are not supported in this browser.
          </p>
        ) : pushState.permission === 'denied' ? (
          <p style={{ fontSize: '13px', color: 'var(--danger)' }}>
            Push notifications are blocked. Please enable them in your browser settings.
          </p>
        ) : (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <label style={{ fontWeight: 500 }}>Push Notifications</label>
              <button
                className={`btn btn-sm ${pushState.subscribed && pushState.enabled ? 'btn-danger' : 'btn-primary'}`}
                onClick={async () => {
                  setPushState(s => ({ ...s, loading: true }));
                  try {
                    if (pushState.subscribed && pushState.enabled) {
                      await unregisterPush();
                      await api.put('/api/push/settings', { pushEnabled: false });
                      setPushState(s => ({ ...s, subscribed: false, enabled: false, loading: false }));
                    } else {
                      await registerPush();
                      await api.put('/api/push/settings', { pushEnabled: true });
                      setPushState(s => ({ ...s, subscribed: true, enabled: true, loading: false, permission: Notification.permission }));
                    }
                  } catch (err) {
                    console.error('Push toggle failed:', err);
                    setPushState(s => ({ ...s, loading: false, permission: Notification.permission }));
                  }
                }}
              >
                {pushState.subscribed && pushState.enabled ? 'Disable' : 'Enable'}
              </button>
              <span className={`badge ${pushState.subscribed && pushState.enabled ? 'badge-success' : 'badge-secondary'}`}>
                {pushState.subscribed && pushState.enabled ? 'Active' : 'Off'}
              </span>
            </div>
            {pushState.subscribed && pushState.enabled && (
              <div style={{ marginBottom: '12px' }}>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={async () => {
                    setTestMsg(null);
                    try {
                      await api.post('/api/push/test');
                      setTestMsg({ type: 'success', text: 'Test notification sent! Check your device.' });
                    } catch (err) {
                      setTestMsg({ type: 'error', text: err.response?.data?.error || 'Failed to send test notification' });
                    }
                  }}
                >
                  Send Test Notification
                </button>
                {testMsg && (
                  <span style={{ marginLeft: '10px', fontSize: '13px', color: testMsg.type === 'success' ? 'var(--success)' : 'var(--danger)' }}>
                    {testMsg.text}
                  </span>
                )}
              </div>
            )}
            <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0 }}>
              Get notified about tasks, alerts, and updates even when Nucleus is closed.
              {/iPhone|iPad/.test(navigator.userAgent) && ' Push notifications on iOS require adding Nucleus to your home screen.'}
            </p>
          </div>
        )}
      </div>

      {/* App Info */}
      <div className="card">
        <h3 className="card-title" style={{ marginBottom: '16px' }}>About Nucleus</h3>
        <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
          <p>Nucleus v1.0.0 - Internal Dashboard for Hot Hands Sports</p>
          <p style={{ marginTop: '8px' }}>All times displayed in Eastern Time (ET)</p>
          <p style={{ marginTop: '4px' }}>Data syncs every 10 minutes from TikTok APIs</p>
        </div>
      </div>
    </div>
  );
}
